import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faFilter } from '@fortawesome/pro-solid-svg-icons';
import classnames from 'classnames';
import isEqual from 'lodash.isequal';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useInfiniteHits } from 'react-instantsearch';
import type { MessageDescriptor } from 'react-intl';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Blockquote } from '@sorare/core/src/atoms/layout/Blockquote';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { LinkBox, LinkOverlay } from '@sorare/core/src/atoms/navigation/Box';
import { BodyM } from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { useInfiniteScroll } from '@sorare/core/src/hooks/useInfiniteScroll';
import { usePrevious } from '@sorare/core/src/hooks/usePrevious';
import type {
  CardHit,
  CardHit as CardHitType,
} from '@sorare/core/src/lib/algolia';
import {
  createCardSorts,
  getUserCardFilters,
  mergeResults,
} from '@sorare/core/src/lib/algolia';
import { glossary } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { CardRowFromHit as CardRow } from '#src/components/card/CardRow/index.tsx';
import { CurrentMarketplaceUserProvider } from '#src/contexts/currentMarketplaceUser/Provider.tsx';
import { FiltersManager } from '#src/search/FiltersManager/index.tsx';
import { FiltersManagerDialog } from '#src/search/FiltersManagerDialog/index.tsx';
import { SearchBox } from '#src/search/SearchBox/index.tsx';
import { SortCards } from '#src/searchCards/SortCards/index.tsx';
import { tradeFilters as FootballTradeFilters } from '#src/searchCards/football/cardFilters.tsx';
import { tradeFilters as MLBTradeFilters } from '#src/searchCards/mlb/cardFilters.tsx';
import { tradeFilters as NBATradeFilters } from '#src/searchCards/nba/cardFilters.tsx';

import type { CardPicker_anyCard } from './__generated__/index.graphql.ts';

type Props = {
  owner: { id: string; slug: string };
  onClose: () => void;
  title: MessageDescriptor;
  selectedCards: CardHitType[];
  confirmSelectedCards: (cards: CardHitType[]) => void;
  counterOfferSport?: Sport;
  maxCards?: number;
  fetchMissingCardsData: (newState: CardHit[]) => Promise<boolean>;
  cardsData: Record<string, CardPicker_anyCard>;
};

type EligibleCardProps = {
  card: CardHitType;
  selectedCards: string[];
  cardData: CardPicker_anyCard | undefined;
  selectCard: (card: CardHitType) => void;
  deselectCard: (card: CardHitType) => void;
};

const CardRoot = styled(LinkBox)`
  width: 100%;
  border-radius: var(--unit);
  cursor: pointer;
  background: var(--c-nd-100);
  outline: 1px solid var(--c-nd-200);
  &.selected {
    background: linear-gradient(
        0deg,
        rgba(var(--c-rgb-blue-400), 0.1),
        rgba(var(--c-rgb-blue-400), 0.1)
      ),
      var(--c-nd-100);
    outline: 1px solid var(--c-blue-400);
  }
`;

const ButtonOverlay = styled(LinkOverlay).attrs({ as: ButtonBase })`
  display: block;
`;

const EligibleCard = ({
  card,
  selectedCards,
  selectCard,
  deselectCard,
  cardData,
}: EligibleCardProps) => {
  const selected = useMemo(
    () => selectedCards.includes(card.slug),
    [selectedCards, card]
  );

  return (
    <CardRoot className={classnames({ selected })}>
      <ButtonOverlay
        aria-label={card.player.display_name}
        disabled={cardData?.sealed}
        onClick={() => {
          if (selected) {
            deselectCard(card);
            return;
          }
          selectCard(card);
        }}
      />
      <CardRow card={card} cardData={cardData} displayMinPrice={false} />
    </CardRoot>
  );
};

type PrivateProps = {
  selectedCards: CardHitType[];
  setSelectedCards: React.Dispatch<React.SetStateAction<CardHitType[]>>;
  maxCards?: number;
  fetchMissingCardsData: (newState: CardHit[]) => Promise<boolean>;
  cardsData: Record<string, CardPicker_anyCard>;
};

const Nothing = () => (
  <Blockquote variant="grey">
    <FormattedMessage defaultMessage="No Cards found" />
  </Blockquote>
);

const Results = styled(Vertical)`
  flex-grow: 1;
`;
const StyledError = styled.span`
  color: var(--c-red-400);
`;
const Root = styled(Vertical)`
  flex-grow: 1;
  min-height: 0px;
`;

const CardPicker = ({
  selectedCards,
  setSelectedCards,
  maxCards,
  fetchMissingCardsData,
  cardsData,
}: PrivateProps) => {
  const [searching, setSearching] = useState<boolean>(false);

  const {
    items,
    results: algoliaResults,
    isLastPage,
    showMore,
  } = useInfiniteHits<CardHitType>();
  const [showMaxCardsError, setShowMaxCardsError] = useState<boolean>(false);

  const previousItemIds = usePrevious(items.map(i => i.slug));

  const hasMore = !isLastPage;

  const refineNext = () => {
    setSearching(true);
    showMore();
  };

  const { InfiniteScrollLoader } = useInfiniteScroll(
    refineNext,
    hasMore,
    searching
  );

  const selectedCardIds = useMemo(
    () => selectedCards.map(card => card.slug),
    [selectedCards]
  );

  const selectCard = useCallback(
    (card: CardHitType) => {
      if (maxCards && selectedCards.length < maxCards) {
        setSelectedCards(cards => [...cards, card]);
      } else {
        setShowMaxCardsError(true);
      }
    },
    [maxCards, selectedCards, setSelectedCards, setShowMaxCardsError]
  );

  const deselectCard = useCallback(
    (card: CardHitType) => {
      const { slug } = card;
      setSelectedCards(cards => cards.filter(c => c.slug !== slug));
      setShowMaxCardsError(false);
    },
    [setSelectedCards]
  );

  const results = mergeResults(items, selectedCards);

  if (
    !isEqual(
      items.map(i => i.slug),
      previousItemIds
    )
  ) {
    const newItems = items.filter(
      item => !previousItemIds?.includes(item.slug)
    );
    fetchMissingCardsData(newItems);
  }

  useEffect(() => {
    setSearching(false);
  }, [algoliaResults?.page]);

  return (
    <Root>
      {!!showMaxCardsError && (
        <StyledError>
          <FormattedMessage
            defaultMessage="You can only select {maxCards} cards"
            values={{ maxCards }}
          />
        </StyledError>
      )}
      {results.length === 0 && !searching && <Nothing />}
      {results.length > 0 && (
        <Results>
          {results.map(card => (
            <EligibleCard
              key={card.slug}
              card={card}
              cardData={cardsData[card.slug]}
              {...{
                selectCard,
                selectedCards: selectedCardIds,
                deselectCard,
              }}
            />
          ))}
          <InfiniteScrollLoader />
        </Results>
      )}
    </Root>
  );
};

const DialogContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 0 var(--triple-unit);
  height: 100%;
  @media ${tabletAndAbove} {
    container-name: dialog-content;
    container-type: normal;
    max-height: 70vh;
  }
`;
const CtaWrapper = styled.div`
  position: sticky;
  bottom: 0;
  box-shadow: 0px 14px 50px rgba(0, 0, 0, 0.2);
  padding: var(--triple-unit);
`;

const Row = styled(Horizontal)`
  align-items: flex-start;
`;

const FilterColumn = styled(Vertical)`
  width: 300px;
  position: sticky;
  top: 0;

  @media ${tabletAndAbove} {
    /* Dialog content height - Header + gap */
    max-height: calc(100cqh - 48px);
    overflow-y: auto;
  }
`;

export const CardPickerDialog = ({
  onClose,
  owner,
  title,
  selectedCards: defaultSelectedCards,
  confirmSelectedCards,
  counterOfferSport,
  maxCards,
  fetchMissingCardsData,
  cardsData,
}: Props) => {
  const { isMe } = useCurrentUserContext();
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const isLaptop = useIsLaptop();
  const [selectedCards, setSelectedCards] =
    useState<CardHitType[]>(defaultSelectedCards);
  const onConfirmCb = useCallback(
    () => confirmSelectedCards(selectedCards),
    [confirmSelectedCards, selectedCards]
  );
  const { sport } = useSportContext();

  const filters = useMemo(
    () => [getUserCardFilters(idFromObject(owner.id))],
    [owner.id]
  );

  const tradeSport = counterOfferSport || sport;

  const tradeFiltersBySport = {
    [Sport.FOOTBALL]: FootballTradeFilters,
    [Sport.BASEBALL]: MLBTradeFilters,
    [Sport.NBA]: NBATradeFilters,
  };

  const tradeSearchFilters = tradeFiltersBySport[tradeSport];

  const cardSorts = createCardSorts({
    withHighestAverageScore: true,
    withHighestPrice: false,
    withEndingSoon: false,
    withNew: true,
    withBestValue: true,
    withLowestPrice: false,
  });

  return (
    <Dialog
      open
      maxWidth="md"
      fullWidth
      onBack={onClose}
      onClose={onClose}
      title={
        <BodyM className="text-center">
          <FormattedMessage {...title} />
        </BodyM>
      }
      footer={
        <CtaWrapper>
          <Button color="primary" size="medium" onClick={onConfirmCb} fullWidth>
            <FormattedMessage {...glossary.done} />
          </Button>
        </CtaWrapper>
      }
    >
      <DialogContent>
        <CurrentMarketplaceUserProvider
          sport={tradeSport}
          withMyBidsBanner={false}
        >
          <SearchCardsContextProvider
            commonMarket={false}
            {...(isMe(owner) ? {} : { galleryOwnerSlug: owner.slug })}
          >
            <InstantCardSearch
              indexes={[cardSorts[0]]}
              analyticsTags={['NewOffer']}
              defaultFilters={filters}
              availableSorts={cardSorts}
              sport={tradeSport}
              defaultHitsPerPage={15}
              attributesToRetrieve={['*']}
            >
              <Vertical gap={2}>
                <Horizontal>
                  <SearchBox />
                  {!!isLaptop && <SortCards indexes={cardSorts} />}
                  {!isLaptop && (
                    <IconButton
                      size="small"
                      color="quaternary"
                      icon={faFilter}
                      onClick={() => setFilterDialogOpen(true)}
                    />
                  )}
                </Horizontal>

                <Row gap={2}>
                  {isLaptop ? (
                    <FilterColumn>
                      <FiltersManager filters={tradeSearchFilters} />
                    </FilterColumn>
                  ) : (
                    <FiltersManagerDialog
                      open={filterDialogOpen}
                      onClose={() => setFilterDialogOpen(false)}
                      filtersGroups={tradeSearchFilters}
                    />
                  )}
                  <CardPicker
                    selectedCards={selectedCards}
                    setSelectedCards={setSelectedCards}
                    maxCards={maxCards}
                    cardsData={cardsData}
                    fetchMissingCardsData={fetchMissingCardsData}
                  />
                </Row>
              </Vertical>
            </InstantCardSearch>
          </SearchCardsContextProvider>
        </CurrentMarketplaceUserProvider>
      </DialogContent>
    </Dialog>
  );
};

CardPickerDialog.fragments = {
  anyCard: gql`
    fragment CardPicker_anyCard on AnyCardInterface {
      slug
      sealed
      ...DirectOffer_CardRow_anyCard
    }
    ${CardRow.fragments.anyCard}
  ` as TypedDocumentNode<CardPicker_anyCard>,
};
